import './ThemeModal.css'

import React, { useEffect, useRef } from 'react'

import { useTheme } from './ThemeProvider'
import { COLOR_LABELS, PRESETS } from './themes'

interface ThemeModalProps {
  onClose: () => void
}

export function ThemeModal({ onClose }: ThemeModalProps) {
  const { theme, setPreset, setColor, reset } = useTheme()
  const overlayRef = useRef<HTMLDivElement>(null)

  // Move focus into the modal on mount so Escape-to-close is reliably reachable
  // by keyboard even when the trigger element kept focus.
  useEffect(() => {
    overlayRef.current?.focus()
  }, [])

  return (
    <div
      ref={overlayRef}
      className="theme-modal-overlay"
      onClick={onClose}
      onKeyDown={(e) => e.key === 'Escape' && onClose()}
      role="presentation"
      tabIndex={-1}
    >
      {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-noninteractive-element-interactions */}
      <div
        className="theme-modal"
        role="dialog"
        aria-modal="true"
        aria-label="Theme"
        onClick={(e) => e.stopPropagation()}
      >
        <h2>Theme</h2>
        <div className="theme-sub">Changes preview live on the board</div>

        <div className="theme-presets">
          {PRESETS.map((p) => (
            <button
              key={p.key}
              type="button"
              className="theme-preset"
              aria-pressed={theme.presetKey === p.key}
              onClick={() => setPreset(p.key)}
            >
              <span className="theme-preset-swatch">
                <span style={{ background: p.colors.lane1 }} />
                <span style={{ background: p.colors.lane2 }} />
                <span style={{ background: p.colors.you }} />
                <span style={{ background: p.colors.opp }} />
              </span>
              {p.name}
            </button>
          ))}
          <button
            type="button"
            className="theme-preset"
            aria-pressed={theme.presetKey === 'custom'}
            disabled
          >
            <span className="theme-preset-swatch">
              <span
                style={{
                  background: 'linear-gradient(90deg,#f88,#8f8,#88f)',
                  flex: 1,
                }}
              />
            </span>
            Custom
          </button>
        </div>

        {COLOR_LABELS.map(({ key, label }) => (
          <label className="theme-row" key={key}>
            <span>{label}</span>
            <input
              type="color"
              aria-label={label}
              value={theme.colors[key]}
              onChange={(e) => setColor(key, e.target.value)}
            />
          </label>
        ))}

        <div className="theme-actions">
          <button type="button" className="theme-reset" onClick={reset}>
            Reset to default
          </button>
          <button type="button" className="theme-done" onClick={onClose}>
            Done
          </button>
        </div>
      </div>
    </div>
  )
}
